import { Form, Button } from "react-bootstrap";
import { useState, useEffect, useRef } from "react";
import { useTranslation, Trans } from "react-i18next";
import Axios from "@/shared/api/axios";
import StatusModal from "@/components/ui/StatusModal";
import LoadingButton from "@/components/ui/LoadingButton";
import DatePicker from "@/components/ui/DatePicker";
import { useDispatch } from "react-redux";
import { setUserToken, setUserId } from "@/shared/store/slices/userSlice";
import { toDateInputValue } from "@/shared/utils/formatDate";

/**
 * Sign up form with all fields visible from the start
 * 註冊表單，所有欄位從一開始就顯示
 */
function SignUpForm({ onShowNickname }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  let token = null;

  // Unified status modal
  const statusModalRef = useRef(null);
  const [currentStatusCode, setCurrentStatusCode] = useState('');

  const [validated, setValidated] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const [isResending, setIsResending] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [form, setForm] = useState({
    email: "",
    password: "",
    code: "",
    username: "",
    birthday: "",
    method: "register",
  });

  // 限制生日18歲以上
  const now = new Date();
  const yearRange = new Date(now.setFullYear(new Date().getFullYear() - 18))
    .toISOString()
    .split("T")[0];

  const handleChange = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });
  };

  // 倒計時功能
  useEffect(() => {
    let timer;
    if (resendCountdown > 0) {
      timer = setTimeout(() => {
        setResendCountdown(resendCountdown - 1);
      }, 1000);
    }
    return () => clearTimeout(timer);
  }, [resendCountdown]);

  // 發送驗證碼函數
  const handleSendVerification = async () => {
    if (resendCountdown > 0 || isResending || !form.email) return;

    setIsResending(true);

    try {
      const formData = await Axios.post("/user/verify-email", {
        email: form.email,
        method: "register"
      });

      const statusCode = formData.data.status_code;

      if (statusCode === "SYSTEM_1000") {
        setResendCountdown(60);
      } else {
        // Show error modal for any non-success status
        setCurrentStatusCode(statusCode);
        statusModalRef.current?.show();
      }
    } catch (error) {
      console.error("Send verification code error:", error);
      // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
      if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
        setCurrentStatusCode(error.httpErrorCode);
        statusModalRef.current?.show();
      }
    } finally {
      setIsResending(false);
    }
  };

  const handleSubmit = async (event) => {
    const formCheck = event.currentTarget;

    event.preventDefault();

    if (formCheck.checkValidity() === false) {
      event.stopPropagation();
      setValidated(true);
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = await Axios.post("/user/register", form);
      const statusCode = formData.data.status_code;

      if (statusCode === "SYSTEM_1000") {
        onShowNickname(true);
        token = formData.data.access_token;
        dispatch(setUserToken(token));
        dispatch(setUserId(formData.data.user_id));
      } else {
        // Show error modal for any non-success status
        setCurrentStatusCode(statusCode);
        statusModalRef.current?.show();
      }
    } catch (error) {
      console.error("Registration error:", error);
      // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
      if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
        setCurrentStatusCode(error.httpErrorCode);
        statusModalRef.current?.show();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Form noValidate validated={validated} onSubmit={handleSubmit}>
        {/* Email */}
        <Form.Group className="mb-3">
          <Form.Control
            onChange={handleChange}
            name="email"
            type="email"
            placeholder={t("email")}
            required
            disabled={resendCountdown > 0}
          />
          <Form.Control.Feedback type="invalid">
            {t("please_valid_email")}
          </Form.Control.Feedback>
        </Form.Group>

        {/* Send Verification Button with Verification Code */}
        <Form.Group className="mb-3">
          <div className="d-flex gap-2">
            <Button
              disabled={resendCountdown > 0 || isResending || !form.email}
              onClick={handleSendVerification}
              className="btn btn-primaryF text-nowrap flex-shrink-0"
              type="button"
              style={{ width: '230px' }}
            >
              {resendCountdown > 0
                ? t("resend_in_seconds", { seconds: resendCountdown })
                : t("send_verification_letter")
              }
            </Button>
            <Form.Control
              onChange={handleChange}
              name="code"
              placeholder={t("verification_code")}
              required
            />
          </div>
          <Form.Control.Feedback type="invalid">
            {t("check_email_code")}
          </Form.Control.Feedback>
        </Form.Group>

        {/* Password */}
        <Form.Group className="mb-3">
          <Form.Control
            onChange={handleChange}
            name="password"
            type="password"
            placeholder={t("password_requirement")}
            autoComplete="new-password"
            pattern="[A-Za-z0-9_@#\-]{8,255}"
            required
          />
          <Form.Control.Feedback type="invalid">
            {t("password_must")}
          </Form.Control.Feedback>
        </Form.Group>

        {/* Username */}
        <Form.Group className="mb-3">
          <Form.Control
            onChange={handleChange}
            name="username"
            placeholder={t("username")}
            required
          />
          <Form.Control.Feedback type="invalid">
            {t("please_user_name")}
          </Form.Control.Feedback>
        </Form.Group>

        {/* Birthday */}
        <Form.Group className="mb-3">
          <div className="d-flex align-items-center gap-3">
            <Form.Label className="mb-0 text-nowrap">{t("birthday")}</Form.Label>
            <DatePicker
              name="birthday"
              value={form.birthday}
              maxDate={yearRange}
              required
              onChange={(date) => setForm({ ...form, birthday: date ? toDateInputValue(date) : "" })}
            />
          </div>
          <Form.Control.Feedback type="invalid">
            {t("please_birthday")}
          </Form.Control.Feedback>
        </Form.Group>

        {/* Terms Agreement */}
        <Form.Check
          type="checkbox"
          id="agree-terms"
          className="mb-3 small"
          required
          label={
            <Trans
              i18nKey="agree_terms_with_links"
              components={{
                1: <a href="/rules" target="_blank" rel="noopener noreferrer" className="link-secondary" />,
                3: <a href="/privacy" target="_blank" rel="noopener noreferrer" className="link-secondary" />,
                5: <a href="/cookies" target="_blank" rel="noopener noreferrer" className="link-secondary" />,
                7: <a href="/terms" target="_blank" rel="noopener noreferrer" className="link-secondary" />,
              }}
            />
          }
        />

        {/* Submit Button */}
        <div>
          <LoadingButton
            className="btn btn-primary btn-lg w-100 mb-3 btn-primaryF"
            type="submit"
            isLoading={isSubmitting}
          >
            {t("signup")}
          </LoadingButton>
        </div>
      </Form>

      {/* Unified Status Modal */}
      <StatusModal ref={statusModalRef} statusCode={currentStatusCode} />
    </>
  );
}

export default SignUpForm;
